import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import StatusBadge from "@/components/common/StatusBadge";
import DataTable from "@/components/common/DataTable";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Flask, Printer, ArrowClockwise, Warning, LockKey } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const CAN_EDIT = ["admin", "owner", "admin_kantor"];
const CAN_DELETE = ["admin", "owner"];
const CAN_LAB = ["admin", "owner", "admin_kantor", "lab"];
const CAN_RESET_SPB = ["admin", "owner"];
const CAN_FORCE_CLOSE = ["admin", "owner"];

const CATEGORIES = ["Raw Material (Oli Bekas)", "Supporting Material", "Lainnya", "Retur"];
const RETUR_MATERIALS = ["Oli Bekas", "Minyak Bakar", "Presol"];
const RETUR_DENSITY_DEFAULT = { "Oli Bekas": 0.87, "Minyak Bakar": 0.92, "Presol": 0.87 };
const RETUR_SOURCES = [
  { v: "customer", l: "Dari Customer (retur DO)" },
  { v: "internal", l: "Dari Internal (produksi/loading)" },
];
const initial = {
  supplier: "", material: "Oli Bekas", category: "Raw Material (Oli Bekas)", po_no: "",
  estimated_date: "", truck_no: "", driver: "", destination_tank: "",
  estimated_qty: 0, notes: "",
  // retur fields
  is_retur: false, retur_source: "customer", retur_do_no: "", retur_density: 0.87,
};

const labInitial = {
  kilogram: 0, density: 0.87, yield_rate: 0, water: 0,
  solar_jelek: 0, kotoran: 0, bensin: 0, notes: "",
};

export default function ReceivingPage() {
  const { user } = useAuth();
  const canEdit = CAN_EDIT.includes(user?.role);
  const canDelete = CAN_DELETE.includes(user?.role);
  const canLab = CAN_LAB.includes(user?.role);
  const canResetSpb = CAN_RESET_SPB.includes(user?.role);
  const canForceClose = CAN_FORCE_CLOSE.includes(user?.role);

  // Force-close override state
  const [forceCloseRow, setForceCloseRow] = useState(null);
  const [forceCloseReason, setForceCloseReason] = useState("");
  const [forceCloseSubmitting, setForceCloseSubmitting] = useState(false);
  const [forceCloseResult, setForceCloseResult] = useState(null);

  const submitForceClose = async () => {
    if (!forceCloseReason.trim() || forceCloseReason.trim().length < 10) {
      toast.error("Alasan wajib diisi minimal 10 karakter untuk keperluan audit");
      return;
    }
    setForceCloseSubmitting(true);
    try {
      const { data } = await api.post(`/receiving/${forceCloseRow.id}/force-close`, {
        reason: forceCloseReason.trim(),
      });
      setForceCloseResult(data);
      setForceCloseRow(null);
      setForceCloseReason("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setForceCloseSubmitting(false);
    }
  };

  // Kriteria untuk tampilkan tombol force-close: lab result ada, timbang lengkap, belum closed
  const canShowForceClose = (r) =>
    canForceClose &&
    r.lab_result && r.gross_in > 0 && r.gross_out > 0 &&
    r.gross_in > r.gross_out &&
    !r.stock_accumulated &&
    r.status !== "closed";

  const resetSpb = async (row) => {
    if (!window.confirm(`Reset SPB untuk ${row.ref_no}?\nSPB akan dihapus & dokumen kembali ke antrian Persetujuan SPB agar bisa diapprove ulang.`)) return;
    try {
      await api.post(`/receiving/${row.id}/spb-reset`);
      toast.success(`SPB ${row.ref_no} berhasil di-reset — dokumen kembali ke antrian Persetujuan SPB`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const [rows, setRows] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [deliveries, setDeliveries] = useState([]); // untuk dropdown "No. DO asal" pada retur
  const [form, setForm] = useState(initial);
  const [labForm, setLabForm] = useState(labInitial);
  const [labFor, setLabFor] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("all");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const setLab = (k) => (v) => setLabForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const load = () => {
    api.get("/receiving").then((r) => setRows(r.data)).catch(() => {});
    api.get("/master/tanks").then((r) => setTanks(r.data)).catch(() => {});
    api.get("/delivery?status=closed").then((r) => setDeliveries(r.data || [])).catch(() => {});
  };
  useAutoRefresh(load);

  const openNew = () => { setForm(initial); setEditingId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({
      supplier: r.supplier, material: r.material, category: r.category, po_no: r.po_no || "",
      estimated_date: r.estimated_date || "", truck_no: r.truck_no || "", driver: r.driver || "",
      destination_tank: r.destination_tank || "", estimated_qty: r.estimated_qty || 0, notes: r.notes || "",
      is_retur: !!r.is_retur,
      retur_source: r.retur_source || "customer",
      retur_do_no: r.retur_do_no || "",
      retur_density: r.retur_density || 0.87,
    });
    setEditingId(r.id); setOpen(true);
  };

  // Toggle Retur: reset material ke default retur, set density default, set kategori "Retur"
  const toggleRetur = (checked) => {
    setForm((f) => {
      if (checked) {
        const m = RETUR_MATERIALS.includes(f.material) ? f.material : "Oli Bekas";
        return {
          ...f,
          is_retur: true,
          material: m,
          category: "Retur",
          retur_density: RETUR_DENSITY_DEFAULT[m] || 0.87,
        };
      }
      return { ...f, is_retur: false, category: "Raw Material (Oli Bekas)" };
    });
  };

  // Ketika material retur berubah, sync density default
  const setReturMaterial = (m) => {
    setForm((f) => ({ ...f, material: m, retur_density: RETUR_DENSITY_DEFAULT[m] || 0.87 }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        estimated_qty: parseFloat(form.estimated_qty) || 0,
        retur_density: form.is_retur ? (parseFloat(form.retur_density) || 0) : 0,
      };
      if (editingId) {
        await api.patch(`/receiving/${editingId}`, payload);
        toast.success("Referensi receiving diperbarui");
      } else {
        await api.post("/receiving", payload);
        toast.success("Referensi receiving dibuat");
      }
      setForm(initial); setEditingId(null); setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Hapus receiving ${row.ref_no}?`)) return;
    try {
      await api.delete(`/receiving/${row.id}`);
      toast.success("Receiving dihapus"); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openLab = (row) => {
    setLabFor(row);
    if (row.lab_result) {
      setLabForm({ ...labInitial, ...row.lab_result });
    } else {
      setLabForm({ ...labInitial, kilogram: row.gross_in ? (row.gross_in - (row.gross_out || 0)) : 0 });
    }
  };

  const submitLab = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(Object.entries(labForm).map(([k, v]) => [k, k === "notes" ? v : parseFloat(v) || 0]));
      payload.notes = labForm.notes;
      await api.post(`/receiving/${labFor.id}/lab`, payload);
      toast.success("Hasil lab tersimpan & stok terakumulasi");
      setLabFor(null); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const filtered = rows.filter((r) => status === "all" || r.status === status);

  return (
    <AppShell
      title="Penerimaan (Receiving)"
      subtitle="Oli Bekas masuk → hasil lab menentukan akumulasi Presol & Minyak Bakar"
      actions={
        canEdit || user?.role === "admin_pabrik" || user?.role === "supervisor" ? (
          <Button onClick={openNew} data-testid="receiving-new-btn" className="bg-sky-600 hover:bg-sky-700 h-10 gap-2">
            <Plus size={16} weight="bold" /> <span className="hidden sm:inline">Referensi Baru</span>
          </Button>
        ) : null
      }
    >
      <div className="flex items-center gap-3 mb-5">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48 h-10" data-testid="rcv-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="weighed_in">Timbang Masuk</SelectItem>
            <SelectItem value="weighed_out">Timbang Keluar</SelectItem>
            <SelectItem value="closed">Selesai</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs font-mono text-slate-500">{filtered.length} baris</span>
      </div>

      {/* Banner: menunggu input lab — alur baru: setelah Timbang MASUK saja */}
      {(() => {
        // Retur tidak butuh Lab, exclude dari pending list
        const pending = rows.filter((r) => r.gross_in && !r.lab_result && !r.is_retur);
        if (pending.length === 0 || !canLab) return null;
        return (
          <div className="mb-4 rounded-xl bg-amber-50 border-l-4 border-amber-500 p-4 flex items-center gap-3">
            <Flask size={28} weight="duotone" className="text-amber-600 shrink-0 animate-pulse" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-amber-900">
                {pending.length} Receiving menunggu input hasil Lab
              </div>
              <div className="text-[12px] text-amber-800">
                Truk sudah Timbang Masuk. Petugas Lab silakan uji kualitas (density, yield %, bensin %) → hasil dikirim ke Approver untuk terbitkan SPB.
              </div>
            </div>
            <button onClick={() => openLab(pending[0])} data-testid="rcv-lab-first"
              className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-md font-semibold text-sm shadow-sm shrink-0 hidden sm:inline-flex items-center gap-2">
              <Flask size={15} weight="fill" /> Input Lab Pertama
            </button>
          </div>
        );
      })()}

      <DataTable
        testid="receiving-table"
        rows={filtered}
        columns={[
          { key: "ref_no", label: "No. Referensi", render: (r) => (
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-slate-900">{r.ref_no}</span>
                {r.is_retur && (
                  <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-orange-100 text-orange-800 border border-orange-300" data-testid={`rcv-retur-badge-${r.ref_no}`}>
                    RETUR
                  </span>
                )}
              </div>
          )},
          { key: "supplier", label: r => r?.is_retur ? "Sumber" : "Supplier", render: (r) => (
              r.is_retur ? (
                <div className="text-[12px] leading-tight">
                  <div className="text-slate-800 font-semibold">{r.supplier}</div>
                  <div className="text-[10px] uppercase tracking-wide text-orange-700">{r.retur_source === "internal" ? "Internal" : "Customer"}{r.retur_do_no ? ` · ${r.retur_do_no}` : ""}</div>
                </div>
              ) : r.supplier
          )},
          { key: "material", label: "Barang" },
          { key: "destination_tank", label: "Tank" },
          { key: "gross_in", label: "Timbang (kg)", render: (r) => <span className="font-mono">{r.gross_in?.toLocaleString?.("id-ID") ?? "—"}</span> },
          { key: "lab", label: "Hasil Lab / SPB", render: (r) => {
              // Retur: skip Lab & SPB, tampilkan konversi liter kalau sudah selesai
              if (r.is_retur) {
                if (r.stock_accumulated) {
                  return (
                    <div className="text-[11px] leading-tight">
                      <div className="text-emerald-700 font-bold">✓ Stok ditambahkan</div>
                      <div className="text-slate-500 font-mono">{(r.net_liter || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 })} L · d {r.retur_density || 0.87}</div>
                    </div>
                  );
                }
                if (r.gross_in && !r.gross_out) {
                  return <span className="text-orange-700 text-[11px] font-semibold">Menunggu Timbang Keluar</span>;
                }
                return <span className="text-slate-400 text-[11px] italic">Menunggu Timbang Masuk</span>;
              }
              if (r.spb_no) {
                return (
                  <div className="text-[11px] leading-tight">
                    <div className="text-emerald-700 font-bold">✓ SPB {r.spb_no}</div>
                    <div className="text-slate-500 font-mono">Yield {(r.lab_result?.yield_rate || 0).toFixed(1)}%</div>
                  </div>
                );
              }
              if (r.status === "spb_rejected") {
                return <span className="text-red-600 text-[11px] font-bold">✗ SPB Ditolak</span>;
              }
              if (r.lab_result) {
                return (
                  <div className="text-[11px] leading-tight">
                    <div className="text-amber-700 font-bold">⌛ Lab siap · Menunggu SPB</div>
                    <div className="text-slate-500 font-mono">Yield {(r.lab_result.yield_rate || 0).toFixed(1)}%</div>
                  </div>
                );
              }
              if (r.gross_in && canLab) {
                return (
                  <button onClick={() => openLab(r)} data-testid={`rcv-lab-btn-${r.ref_no}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-bold uppercase tracking-wide shadow-sm animate-pulse">
                    <Flask size={13} weight="fill" /> Input Lab
                  </button>
                );
              }
              return <span className="text-slate-400 text-[11px] italic">Menunggu Timbang Masuk</span>;
          }},
          { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          { key: "actions", label: "", render: (r) => (
            <div className="flex items-center gap-1">
              {r.spb_no && (
                <a href={`/spb/${r.id}`} target="_blank" rel="noreferrer" data-testid={`rcv-spb-print-${r.ref_no}`}
                  className="text-slate-800 hover:bg-amber-50 p-1.5 rounded-md" title={`Cetak SPB ${r.spb_no}`}><Printer size={15} weight="duotone" /></a>
              )}
              {canResetSpb && r.spb_no && !r.stock_accumulated && !r.is_retur && (
                <button onClick={() => resetSpb(r)} data-testid={`rcv-spb-reset-${r.ref_no}`}
                  className="text-orange-700 hover:bg-orange-50 p-1.5 rounded-md" title="Reset SPB — kembalikan ke antrian persetujuan"><ArrowClockwise size={15} weight="duotone" /></button>
              )}
              {canShowForceClose(r) && !r.is_retur && (
                <button onClick={() => { setForceCloseRow(r); setForceCloseReason(""); }} data-testid={`rcv-force-close-${r.ref_no}`}
                  className="text-rose-700 hover:bg-rose-50 p-1.5 rounded-md ring-1 ring-rose-200" title="Force-Close (Admin Override) — SPB darurat + akumulasi stok"><LockKey size={15} weight="fill" /></button>
              )}
              {canLab && r.lab_result && !r.is_retur && (
                <button onClick={() => openLab(r)} data-testid={`rcv-lab-view-${r.ref_no}`}
                  className="text-emerald-700 hover:bg-emerald-50 p-1.5 rounded-md" title="Lihat / Edit Hasil Lab"><Flask size={15} weight="duotone" /></button>
              )}
              {canEdit && (
                <button onClick={() => openEdit(r)} data-testid={`rcv-edit-${r.ref_no}`}
                  className="text-sky-700 hover:text-sky-900 p-1.5 hover:bg-sky-50 rounded-md" title="Edit"><PencilSimple size={15} weight="duotone" /></button>
              )}
              {canDelete && (
                <button onClick={() => remove(r)} data-testid={`rcv-del-${r.ref_no}`}
                  className="text-red-600 hover:text-red-800 p-1.5 hover:bg-red-50 rounded-md" title="Hapus"><Trash size={15} weight="duotone" /></button>
              )}
            </div>
          )},
        ]}
      />

      {/* Receiving Ref Form */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">{editingId ? "Edit Referensi Receiving" : "Referensi Receiving Baru"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} data-testid="receiving-form" className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            {/* Toggle Barang Retur */}
            <div className="md:col-span-2 rounded-lg border border-orange-200 bg-orange-50/60 p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[13px] font-bold text-orange-900 flex items-center gap-2">
                  <ArrowClockwise size={16} weight="fill" className="text-orange-600" />
                  Barang Retur
                </div>
                <p className="text-[11px] text-orange-800 mt-0.5">
                  Aktifkan untuk barang yang <b>dikembalikan</b> ke pabrik (dari customer atau internal). Alur ringkas: Timbang Masuk → Timbang Keluar → stok masuk ke tangki. Skip Lab & SPB.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0" data-testid="rcv-retur-toggle">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={!!form.is_retur}
                  onChange={(e) => toggleRetur(e.target.checked)}
                />
                <div className="w-11 h-6 bg-slate-300 peer-focus:ring-2 peer-focus:ring-orange-400 rounded-full peer peer-checked:bg-orange-500 relative transition-colors">
                  <span className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_retur ? 'translate-x-5' : ''}`} />
                </div>
              </label>
            </div>

            <Field label={form.is_retur ? "Nama Customer / Sumber Internal" : "Supplier"} required>
              <Input data-testid="rcv-supplier" value={form.supplier} onChange={set("supplier")} required
                placeholder={form.is_retur ? (form.retur_source === "internal" ? "misal: Divisi Produksi" : "misal: PT ABC Sejahtera") : ""} />
            </Field>

            {form.is_retur ? (
              <Field label="Barang Retur" required>
                <Select value={form.material} onValueChange={setReturMaterial}>
                  <SelectTrigger className="h-10" data-testid="rcv-material-retur"><SelectValue /></SelectTrigger>
                  <SelectContent>{RETUR_MATERIALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            ) : (
              <Field label="Nama Barang" required>
                <Input data-testid="rcv-material" value={form.material} onChange={set("material")} required />
              </Field>
            )}

            {form.is_retur && (
              <>
                <Field label="Sumber Retur" required>
                  <Select value={form.retur_source} onValueChange={set("retur_source")}>
                    <SelectTrigger className="h-10" data-testid="rcv-retur-source"><SelectValue /></SelectTrigger>
                    <SelectContent>{RETUR_SOURCES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="No. DO Asal (opsional)">
                  <Input
                    data-testid="rcv-retur-do"
                    list="rcv-retur-do-list"
                    value={form.retur_do_no}
                    onChange={set("retur_do_no")}
                    placeholder="Ketik / pilih DO yang selesai"
                    className="font-mono"
                  />
                  <datalist id="rcv-retur-do-list">
                    {deliveries.map((d) => (
                      <option key={d.id} value={d.do_no}>{`${d.do_no} · ${d.customer || ''} · ${d.product || ''}`}</option>
                    ))}
                  </datalist>
                </Field>
                <Field label={`Density (kg/L) — default ${RETUR_DENSITY_DEFAULT[form.material] || 0.87}`}>
                  <Input
                    data-testid="rcv-retur-density"
                    type="number" step="0.001"
                    value={form.retur_density}
                    onChange={set("retur_density")}
                    className="font-mono"
                    placeholder={String(RETUR_DENSITY_DEFAULT[form.material] || 0.87)}
                  />
                </Field>
              </>
            )}

            {!form.is_retur && (
              <Field label="Kategori">
                <Select value={form.category} onValueChange={set("category")}>
                  <SelectTrigger className="h-10" data-testid="rcv-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.filter(c => c !== "Retur").map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}

            <Field label="PO / SJ"><Input data-testid="rcv-po" value={form.po_no} onChange={set("po_no")} /></Field>
            <Field label="Estimasi Tanggal"><Input data-testid="rcv-date" type="date" value={form.estimated_date} onChange={set("estimated_date")} /></Field>
            <Field label="Estimasi Qty (Liter)"><Input data-testid="rcv-qty" type="number" step="0.01" value={form.estimated_qty} onChange={set("estimated_qty")} className="font-mono" /></Field>
            <Field label="Nomor Truck"><Input data-testid="rcv-truck" value={form.truck_no} onChange={set("truck_no")} className="font-mono" /></Field>
            <Field label="Driver"><Input data-testid="rcv-driver" value={form.driver} onChange={set("driver")} /></Field>
            <Field label={form.is_retur ? "Tank Tujuan (Wajib)" : "Tank Tujuan"} required={form.is_retur}>
              <Select value={form.destination_tank} onValueChange={set("destination_tank")}>
                <SelectTrigger className="h-10" data-testid="rcv-tank"><SelectValue placeholder="Pilih tank" /></SelectTrigger>
                <SelectContent>
                  {tanks
                    .filter((t) => t.type !== "production_area")
                    .map((t) => {
                      const match = form.is_retur && (t.product || "").toLowerCase() === (form.material || "").toLowerCase();
                      return (
                        <SelectItem key={t.id} value={t.code}>
                          {match ? "★ " : ""}{t.name}{t.product ? ` · ${t.product}` : ""}
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
            </Field>
            <div className="md:col-span-2"><Field label="Catatan"><Textarea data-testid="rcv-notes" rows={2} value={form.notes} onChange={set("notes")} /></Field></div>
            <div className="md:col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" data-testid="rcv-submit" disabled={submitting} className={form.is_retur ? "bg-orange-600 hover:bg-orange-700 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}>
                {submitting ? "Menyimpan…" : editingId ? "Perbarui" : (form.is_retur ? "Simpan Retur" : "Simpan Referensi")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lab Result Dialog */}
      <Dialog open={!!labFor} onOpenChange={(o) => !o && setLabFor(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <Flask size={20} weight="duotone" className="text-amber-600" />
              Hasil Lab — {labFor?.ref_no}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitLab} data-testid="lab-form" className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Kilogram (timbangan)" required>
                <Input data-testid="lab-kg" type="number" step="0.01" value={labForm.kilogram} onChange={setLab("kilogram")} required className="font-mono h-11" />
              </Field>
              <Field label="Density (kg/L)" required>
                <Input data-testid="lab-density" type="number" step="0.001" value={labForm.density} onChange={setLab("density")} required className="font-mono h-11" placeholder="0.87" />
              </Field>
            </div>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-900">
              <b>Konversi ke Liter:</b> {labForm.kilogram} kg ÷ {labForm.density || "?"} = <b className="font-mono text-base">
                {parseFloat(labForm.density) > 0 ? (parseFloat(labForm.kilogram||0) / parseFloat(labForm.density)).toLocaleString("id-ID", { maximumFractionDigits: 0 }) : "—"} L
              </b> Oli Bekas
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Yield Rate % (= % Presol)" required><Input data-testid="lab-yield" type="number" step="0.01" value={labForm.yield_rate} onChange={setLab("yield_rate")} className="font-mono" /></Field>
              <Field label="Bensin % (= % Minyak Bakar)"><Input data-testid="lab-bensin" type="number" step="0.01" value={labForm.bensin} onChange={setLab("bensin")} className="font-mono" /></Field>
              <Field label="Water %"><Input data-testid="lab-water" type="number" step="0.01" value={labForm.water} onChange={setLab("water")} className="font-mono" /></Field>
              <Field label="Solar Jelek %"><Input data-testid="lab-solar" type="number" step="0.01" value={labForm.solar_jelek} onChange={setLab("solar_jelek")} className="font-mono" /></Field>
              <Field label="Kotoran %"><Input data-testid="lab-kotoran" type="number" step="0.01" value={labForm.kotoran} onChange={setLab("kotoran")} className="font-mono" /></Field>
            </div>
            <Field label="Catatan Lab"><Textarea data-testid="lab-notes" rows={2} value={labForm.notes} onChange={setLab("notes")} /></Field>
            <LabPreview labForm={labForm} />
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setLabFor(null)}>Batal</Button>
              <Button type="submit" data-testid="lab-submit" disabled={submitting} className="bg-amber-600 hover:bg-amber-700 text-white">
                {submitting ? "Menyimpan…" : "Simpan Hasil Lab"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Force-Close (Admin Override) */}
      <Dialog open={!!forceCloseRow} onOpenChange={(o) => { if (!o && !forceCloseSubmitting) { setForceCloseRow(null); setForceCloseReason(""); } }}>
        <DialogContent className="max-w-lg" data-testid="rcv-force-close-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <LockKey size={22} weight="fill" className="text-rose-600" />
              Force-Close — Admin Override
            </DialogTitle>
            <DialogDescription className="text-[13px] pt-2">
              Terbitkan SPB darurat & tutup dokumen ini walau approver tidak available. Aksi ini <b>tercatat lengkap</b> di edit_logs dengan alasan Anda.
            </DialogDescription>
          </DialogHeader>
          {forceCloseRow && (
            <div className="space-y-3 mt-2">
              <div className="rounded-lg border-2 border-rose-200 bg-rose-50 p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 mb-1">
                  <Warning size={12} weight="fill" className="inline mr-1" /> Detail Dokumen
                </div>
                <div className="grid grid-cols-2 gap-y-1 text-[12px]">
                  <span className="text-slate-500">No. Referensi:</span>
                  <span className="font-mono font-bold">{forceCloseRow.ref_no}</span>
                  <span className="text-slate-500">Supplier:</span>
                  <span className="font-medium">{forceCloseRow.supplier}</span>
                  <span className="text-slate-500">Gross In / Out:</span>
                  <span className="font-mono">{forceCloseRow.gross_in?.toLocaleString("id-ID")} / {forceCloseRow.gross_out?.toLocaleString("id-ID")} kg</span>
                  <span className="text-slate-500 font-bold">Net (kg):</span>
                  <span className="font-mono font-bold text-emerald-700">{((forceCloseRow.gross_in || 0) - (forceCloseRow.gross_out || 0)).toLocaleString("id-ID")}</span>
                  <span className="text-slate-500">Yield / Bensin:</span>
                  <span className="font-mono">{forceCloseRow.lab_result?.yield_rate}% / {forceCloseRow.lab_result?.bensin}%</span>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-700">
                  Alasan Override <span className="text-rose-500">*</span> (min. 10 karakter)
                </label>
                <Textarea
                  data-testid="rcv-force-close-reason"
                  rows={3}
                  value={forceCloseReason}
                  onChange={(e) => setForceCloseReason(e.target.value)}
                  placeholder="Contoh: Approver Pak Ahmad tidak dapat dihubungi sejak jam 14:00, truk sudah selesai timbang keluar 16:30, tidak bisa menunggu lebih lama."
                  className="mt-1 text-[13px]"
                />
                <div className="text-[10px] text-slate-400 mt-1">{forceCloseReason.length} / min. 10 karakter</div>
              </div>
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11.5px] text-amber-900">
                <b>Efek:</b> SPB otomatis di-generate dengan prefix <code className="bg-white px-1 rounded">SPB-OVR-</code>, stok Presol & Minyak Bakar terakumulasi sesuai lab. Status → <b>closed</b>.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 mt-3 grid grid-cols-2 sm:flex sm:justify-end">
            <Button variant="outline" disabled={forceCloseSubmitting}
              onClick={() => { setForceCloseRow(null); setForceCloseReason(""); }}
              data-testid="rcv-force-close-cancel" className="h-11">Batal</Button>
            <Button onClick={submitForceClose} disabled={forceCloseSubmitting || forceCloseReason.trim().length < 10}
              data-testid="rcv-force-close-submit"
              className="h-11 bg-rose-600 hover:bg-rose-700 text-white gap-2">
              <LockKey size={16} weight="fill" />
              {forceCloseSubmitting ? "Memproses…" : "Force-Close Sekarang"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog hasil Force-Close */}
      <Dialog open={!!forceCloseResult} onOpenChange={(o) => !o && setForceCloseResult(null)}>
        <DialogContent className="max-w-md" data-testid="rcv-force-close-result">
          <div className="text-center py-2">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <LockKey size={44} weight="fill" className="text-emerald-600" />
            </div>
            <DialogTitle className="font-heading text-xl mb-2">Force-Close Berhasil</DialogTitle>
            {forceCloseResult?.spb_no && (
              <div className="inline-block font-mono font-bold text-base px-3 py-1.5 rounded-md border-2 bg-rose-50 border-rose-300 text-rose-800 mb-3">
                {forceCloseResult.spb_no}
              </div>
            )}
            <DialogDescription className="text-[13px] text-slate-700 leading-relaxed px-2">
              {forceCloseResult?.message}
            </DialogDescription>
            {forceCloseResult && (
              <div className="mt-3 text-[12px] grid grid-cols-3 gap-2 border-t pt-3">
                <div><div className="text-[10px] text-slate-500 uppercase">Net</div><div className="font-mono font-bold">{forceCloseResult.net_kg?.toLocaleString("id-ID")} kg</div></div>
                <div><div className="text-[10px] text-slate-500 uppercase">Presol</div><div className="font-mono font-bold text-amber-700">{forceCloseResult.presol_qty?.toLocaleString("id-ID")} L</div></div>
                <div><div className="text-[10px] text-slate-500 uppercase">M. Bakar</div><div className="font-mono font-bold text-amber-700">{forceCloseResult.minyak_bakar_qty?.toLocaleString("id-ID")} L</div></div>
              </div>
            )}
          </div>
          <DialogFooter className="mt-2">
            <Button onClick={() => setForceCloseResult(null)} data-testid="rcv-force-close-result-ok"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold">OK, Mengerti</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function LabPreview({ labForm }) {
  const kg = parseFloat(labForm.kilogram || 0);
  const d = parseFloat(labForm.density || 0);
  const l = d > 0 ? kg / d : 0;
  const p = l * (parseFloat(labForm.yield_rate) || 0) / 100;
  const m = l * (parseFloat(labForm.bensin) || 0) / 100;
  const fmt = (n) => n.toLocaleString("id-ID", { maximumFractionDigits: 0 });
  return (
    <div className="rounded-md bg-sky-50 border border-sky-200 p-3 text-[12px] text-sky-900 space-y-1">
      <b>Akumulasi Stok Otomatis (Liter):</b>
      <div>Oli Bekas = <b>{fmt(l)} L</b> ({fmt(kg)} kg ÷ {d || "?"})</div>
      <div>Presol = {fmt(l)} × {labForm.yield_rate}% = <b>{fmt(p)} L</b></div>
      <div>Minyak Bakar = {fmt(l)} × {labForm.bensin}% = <b>{fmt(m)} L</b></div>
      <div className="text-[10px] text-sky-700 mt-1.5 italic">Water, Solar Jelek, Kotoran hanya metrik kualitas — tidak menambah stok.</div>
    </div>
  );
}
